import { includes, pickBy } from 'lodash';
import { generateKeys, generateLocalization } from '../utils';
import { appropNatureOfTransactions, appropOriginOfFunds, appropTestAccountTurnover, appropTestAnnualIncome, appropTestEmployerCategory, appropTestExpectedDeposit, appropTestIsUsReportable, appropTestPoliticallyExposed, appropTestSourceOfFunds, appropTestTinReason, profileNetWorth, profileTransactionPurpose } from './appropTest';
import { companies } from './companies';
import { countries } from './countries';
export const tooltipsEp = generateLocalization('tooltipsEp', generateKeys({
    usCitizen: {
        value: 'usCitizen',
        localization: { id: '', defaultMessage: '' },
        label: `The term "United States person" means: <br />
    • A citizen or resident of the United States <br />
    • An individual born in United States <br />
    • An individual whose parent is a US citizen <br />
    • An individual born in Puerto Rico, Guam or US Virgin Islands <br />
    • A former alien who has been naturalized as a US citizen(the holder of the GREEN CARD)`,
    },
    tin: {
        value: 'tin',
        localization: { id: '', defaultMessage: '' },
        label: `To help protect the integrity of tax systems, governments around the world is introducing a new
    information-gathering and reporting requirement for financial institutions. This is known as the Common
    Reporting Standard (the CRS). Under the CRS, we are required to determine where an entity is 'tax resident'
    (this will usually be where you are liable to pay income taxes). If you are a tax resident outside the country
    where your account is held, we may need to give the national tax authority this information, along with
    information relating to your accounts. That may then be shared between different countries' tax authorities.
    Completing this form will ensure that we hold accurate and up to date information about your tax residency.
    If your circumstances change and any of the information provided in this form becomes incorrect, please let us
    know immediately and provide an updated Self-Certification.
    `,
    },
    pep: {
        value: 'peo',
        localization: { id: '', defaultMessage: '' },
        label: `The meaning 'Politically Exposed Persons' includes the following natural persons who are or have been
    entrusted with prominent public functions in any country. <br />The term PEP also includes family members or known
    employees of a person in a politically exposed position:<br />
    • heads of State, heads of government, ministers and deputy or assistant ministers;
    members of parliaments;<br />
    • members of supreme courts, constitutional courts or of other high-level
    judicial bodies whose decisions are not subject to further appeal, except in exceptional circumstances;<br />
    • members of courts of auditors or of the boards of central banks;<br />
    • ambassadors and high ranking officers in the armed forces;<br />
    • members of the administrative, management or supervisory bodies of State-owned enterprises;<br />
    • directors, deputy directors and members of the board or equivalent function of an international organization;<br />
    • mayors.
    `,
    },
}));
const getApplicableOptions = (options, version) => {
    return pickBy(options, (o) => !o.applicableVersions || includes(o.applicableVersions, version));
};
export const economicProfileQuestions = generateLocalization('EconomicProfile', generateKeys({
    transactionPurpose: {
        value: 'transactionPurpose',
        localization: { id: '', defaultMessage: '' },
        order: 1,
        label: 'What is your intended purpose of transactions?',
        question: 'transactionPurposeQuestion',
        optionsEnumKey: 'profileTransactionPurpose',
        options: getApplicableOptions(profileTransactionPurpose, 2),
        mandatory: () => true,
        relevant: () => true,
        company: [companies.bdSwiss, companies.swissMarkets],
        dropdown: true,
    },
    transactionPurposeClarify: {
        value: 'transactionPurposeClarify',
        localization: { id: '', defaultMessage: '' },
        order: 2,
        label: 'Please clarify',
        question: 'pleaseClarifyQuestion',
        mandatory: (answers) => answers.transactionPurpose === profileTransactionPurpose.other.value,
        relevant: (answers) => answers.transactionPurpose === profileTransactionPurpose.other.value,
        textfield: true,
        company: [companies.bdSwiss, companies.swissMarkets],
    },
    approxYearlyIncome: {
        value: 'approxYearlyIncome',
        localization: { id: '', defaultMessage: '' },
        order: 3,
        label: 'What is your approximate yearly income in Euro?',
        question: 'approxYearlyIncomeQuestion',
        description: 'approxYearlyIncomeDesc',
        optionsEnumKey: 'appropTestAnnualIncome',
        options: getApplicableOptions(appropTestAnnualIncome, 1),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    approxNetWorth: {
        value: 'approxNetWorth',
        localization: { id: '', defaultMessage: '' },
        order: 4,
        label: 'What is your approximate total net worth in Euro?',
        question: 'approxNetWorthQuestion',
        description: 'approxNetWorthDesc',
        optionsEnumKey: 'profileNetWorth',
        options: getApplicableOptions(profileNetWorth, 2),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    sourceOfFunds: {
        value: 'sourceOfFunds',
        localization: { id: '', defaultMessage: '' },
        order: 5,
        label: 'What is the source of the Funds you wish to deposit?',
        question: 'sourceOfFundsQuestion',
        optionsEnumKey: 'appropTestSourceOfFunds',
        options: getApplicableOptions(appropTestSourceOfFunds, 1),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    sourceOfFundsClarify: {
        value: 'sourceOfFundsClarify',
        localization: { id: '', defaultMessage: '' },
        order: 6,
        label: 'Please clarify',
        question: 'pleaseClarifyQuestion',
        mandatory: (answers) => answers.sourceOfFunds === appropTestSourceOfFunds.other.value,
        relevant: (answers) => answers.sourceOfFunds === appropTestSourceOfFunds.other.value,
        textfield: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    jobTitle: {
        value: 'jobTitle',
        localization: { id: '', defaultMessage: '' },
        order: 7,
        label: 'In what industry are you currently working in?',
        question: 'jobTitleQuestion',
        optionsEnumKey: 'appropTestEmployerCategory',
        options: getApplicableOptions(appropTestEmployerCategory, 2),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    approxExpectedDeposit: {
        value: 'approxExpectedDeposit',
        localization: { id: '', defaultMessage: '' },
        order: 8,
        label: 'Estimated amount to deposit (yearly)?',
        question: 'approxExpectedDepositQuestion',
        optionsEnumKey: 'appropTestExpectedDeposit',
        options: getApplicableOptions(appropTestExpectedDeposit, 2),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.swissMarkets],
    },
    originOfFunds: {
        value: 'originOfFunds',
        localization: { id: '', defaultMessage: '' },
        order: 9,
        label: 'What is the origin of funds?',
        question: 'originOfFunds',
        optionsEnumKey: 'appropOriginOfFunds',
        options: getApplicableOptions(appropOriginOfFunds, 1),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.swissMarkets],
    },
    natureOfTransactions: {
        value: 'natureOfTransactions',
        localization: { id: '', defaultMessage: '' },
        order: 10,
        label: 'What is the nature of transactions?',
        question: 'natureOfTransactions',
        optionsEnumKey: 'appropNatureOfTransactions',
        options: getApplicableOptions(appropNatureOfTransactions, 1),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.swissMarkets],
    },
    natureOfTransactionsClarify: {
        value: 'natureOfTransactionsClarify',
        localization: { id: '', defaultMessage: '' },
        order: 11,
        label: 'Please clarify',
        question: 'pleaseClarifyQuestion',
        mandatory: (answers) => answers.natureOfTransactions === 'other',
        relevant: (answers) => answers.natureOfTransactions === 'other',
        textfield: true,
        company: [companies.bdSwiss, companies.swissMarkets],
    },
    politicallyExposed: {
        value: 'politicallyExposed',
        localization: { id: '', defaultMessage: '' },
        order: 12,
        label: 'Are you a politically exposed person?',
        question: 'politicallyExposedQuestion',
        optionsEnumKey: 'appropTestPoliticallyExposed',
        options: getApplicableOptions(appropTestPoliticallyExposed, 1),
        description: 'pepDesc',
        mandatory: () => true,
        relevant: () => true,
        alert: (value) => appropTestPoliticallyExposed.yes.value === value,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
        tooltip: tooltipsEp.pep,
    },
    usCitizen: {
        value: 'usCitizen',
        localization: { id: '', defaultMessage: '' },
        order: 13,
        label: 'Are you a US citizen or resident?',
        question: 'usCitizen',
        sectionTitle: 'usTitle',
        optionsEnumKey: 'appropTestIsUsReportable',
        options: getApplicableOptions(appropTestIsUsReportable, 1),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
        tooltip: tooltipsEp.usCitizen,
    },
    taxJurisdictionCountry: {
        value: 'taxJurisdictionCountry',
        localization: { id: '', defaultMessage: '' },
        order: 14,
        label: 'Country/Jurisdiction of Tax Residence?',
        question: 'taxJurisdictionCountry',
        optionsEnumKey: 'countries',
        options: pickBy(countries, (o) => o.value !== 'US' && !o.hidden),
        mandatory: (answers) => answers.usCitizen !== appropTestIsUsReportable.yes.value,
        relevant: (answers) => answers.usCitizen !== appropTestIsUsReportable.yes.value,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    tinReason: {
        value: 'tinReason',
        localization: { id: '', defaultMessage: '' },
        order: 15,
        label: 'If Tax Identification Number is not available, please select a reason:',
        question: 'tinReason',
        optionsEnumKey: 'appropTestTinReason',
        options: getApplicableOptions(appropTestTinReason, 1),
        mandatory: (answers) => answers.usCitizen !== appropTestIsUsReportable.yes.value,
        relevant: (answers) => answers.usCitizen !== appropTestIsUsReportable.yes.value,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
        tooltip: tooltipsEp.tin,
    },
    tin: {
        value: 'tin',
        localization: { id: '', defaultMessage: '' },
        order: 16,
        label: 'Taxpayer Identification Number (TIN)',
        question: 'tinQuestion',
        mandatory: (answers) => answers.tinReason === 'iHaveTin',
        relevant: (answers) => answers.tinReason === 'iHaveTin',
        textfield: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    tinClarify: {
        value: 'tinClarify',
        localization: { id: '', defaultMessage: '' },
        order: 17,
        label: 'Please clarify',
        question: 'pleaseClarifyQuestion',
        mandatory: (answers) => answers.tinReason === 'unableToObtain',
        relevant: (answers) => answers.tinReason === 'unableToObtain',
        textfield: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    approxAccountTurnover: {
        value: 'approxAccountTurnover',
        localization: { id: '', defaultMessage: '' },
        order: 18,
        label: 'Estimated account turnover (yearly)?',
        question: 'approxAccountTurnoverQuestion',
        optionsEnumKey: 'appropTestAccountTurnover',
        options: getApplicableOptions(appropTestAccountTurnover, 2),
        mandatory: () => true,
        relevant: () => true,
        disable: true,
        dropdown: true,
        company: [companies.bdSwiss, companies.bdSwissMauritius, companies.bdSwissVanuatu,
            companies.swissMarkets, companies.swissMarketsMauritius, companies.swissMarketsVanuatu,
            companies.bdSwissSouthAfrica, companies.bdSwissComoros],
    },
    expectedCountryOfOriginDestinationFunds: {
        value: 'expectedCountryOfOriginDestinationFunds',
        localization: { id: '', defaultMessage: '' },
        order: 19,
        label: 'Expected Country of Origin and destination of funds?',
        question: 'expectedCountryOfOriginDestinationFunds',
        optionsEnumKey: 'countries',
        options: pickBy(countries, (o) => !o.forbidden || !o.hidden),
        mandatory: () => true,
        relevant: () => true,
        dropdown: true,
        company: [companies.bdSwiss, companies.swissMarkets],
    },
}));
